import type {
  IPrimitivePaneView,
  ISeriesApi,
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
} from "lightweight-charts";

interface BitmapTarget {
  useMediaCoordinateSpace(fn: (scope: {
    context: CanvasRenderingContext2D;
  }) => void): void;
}

export interface RangeBarPrimitiveDatum {
  startTime: Time;
  endTime: Time;
  open: number;
  high: number;
  low: number;
  close: number;
  color: string;
}

/** Draws a range candle across every logical minute slot owned by that closed bar. */
export class RangeBarPrimitive implements ISeriesPrimitive<Time> {
  private attachedTo: SeriesAttachedParameter<Time> | null = null;
  private bars: readonly RangeBarPrimitiveDatum[] = [];
  private sessionBoundary: { time: Time; color: string } | null = null;
  private readonly view: IPrimitivePaneView = {
    zOrder: () => "normal",
    renderer: () => ({ draw: (target) => this.draw(target as unknown as BitmapTarget) }),
  };

  constructor(
    private readonly series: ISeriesApi<"Candlestick", Time>,
    private readonly onWidths?: (widths: readonly number[]) => void,
  ) {}

  attached(param: SeriesAttachedParameter<Time>) {
    this.attachedTo = param;
  }

  detached() {
    this.attachedTo = null;
  }

  update(bars: readonly RangeBarPrimitiveDatum[]) {
    this.bars = bars;
    this.attachedTo?.requestUpdate();
  }

  setSessionBoundary(time: Time | null, color = "var(--muted)") {
    this.sessionBoundary = time === null ? null : { time, color };
    this.attachedTo?.requestUpdate();
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return [this.view];
  }

  private draw(target: BitmapTarget) {
    const attached = this.attachedTo;
    if (!attached || !this.bars.length) return;
    const timeScale = attached.chart.timeScale();
    const visible = timeScale.getVisibleLogicalRange();
    const visibleSlots = visible === null ? 0 : Number(visible.to) - Number(visible.from);
    const slotWidth = visibleSlots > 0 ? timeScale.width() / visibleSlots : 6;

    target.useMediaCoordinateSpace(({ context }) => {
      const widths: number[] = [];
      context.save();
      for (const bar of this.bars) {
        const start = timeScale.timeToCoordinate(bar.startTime);
        const end = timeScale.timeToCoordinate(bar.endTime);
        const open = this.series.priceToCoordinate(bar.open);
        const high = this.series.priceToCoordinate(bar.high);
        const low = this.series.priceToCoordinate(bar.low);
        const close = this.series.priceToCoordinate(bar.close);
        if ([start, end, open, high, low, close].some((value) => value === null)) continue;

        const left = start! - slotWidth * .4;
        const right = end! + slotWidth * .4;
        const center = (left + right) / 2;
        const bodyTop = Math.min(open!, close!);
        const bodyHeight = Math.max(1, Math.abs(close! - open!));
        widths.push(right - left);
        context.strokeStyle = bar.color;
        context.lineWidth = 1;
        context.beginPath();
        context.moveTo(center, high!);
        context.lineTo(center, low!);
        context.stroke();
        context.fillStyle = bar.color;
        context.fillRect(left, bodyTop, Math.max(1, right - left), bodyHeight);
      }
      if (this.sessionBoundary !== null) {
        const x = timeScale.timeToCoordinate(this.sessionBoundary.time);
        if (x !== null) {
          context.strokeStyle = this.sessionBoundary.color;
          context.lineWidth = 1;
          context.beginPath();
          context.moveTo(x, 0);
          context.lineTo(x, context.canvas.height);
          context.stroke();
        }
      }
      context.restore();
      this.onWidths?.(widths);
    });
  }
}
